// EventFormLogic.tsx
import { keyframes } from "@emotion/react";
import { useSnackbar } from "notistack";
import { useEffect, useState, useCallback } from "react";

import { Event } from "0.Mine/3.Interfaces/Event";

// Define fancy animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(5px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1300, // Lower than MUI's picker popup (which is typically 1400)
    padding: "16px", // Add padding for mobile devices
    transition: "all 0.3s ease-in-out",
  },
  container: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    width: "100%",
    maxWidth: 700, // Enlarged from 400
    overflow: "hidden",
    animation: `${fadeIn} 0.3s ease-out forwards`,
    border: "1px solid rgba(255,255,255,0.1)",
    position: "relative", // Ensure proper stacking context
    zIndex: 1301, // Higher than overlay but lower than picker
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "linear-gradient(90deg, #3f51b5 0%, #5c6bc0 100%)",
    color: "#fff",
    padding: "16px 24px",
    borderBottom: "1px solid rgba(0,0,0,0.1)",
  },
  content: {
    padding: "24px",
    maxHeight: "calc(90vh - 180px)",
    overflow: "auto",
  },
  label: {
    display: "block",
    color: "#5c6bc0",
    fontSize: "0.875rem",
    fontWeight: 600,
    marginBottom: "6px",
    transition: "color 0.2s ease",
    animation: `${slideIn} 0.4s ease-out forwards`,
    animationDelay: "0.1s",
  },
  textField: {
    marginBottom: "20px",
    transition: "all 0.2s ease-in-out",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "&:hover fieldset": {
        borderColor: "#3f51b5",
      },
    },
    animation: `${slideIn} 0.4s ease-out forwards`,
    animationDelay: "0.15s",
  },
  gridTwoColumns: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "20px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "24px",
    padding: "16px 24px",
    borderTop: "1px solid rgba(0,0,0,0.07)",
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  cancelButton: {
    textTransform: "none",
    color: "#5c6bc0",
    borderColor: "rgba(92, 107, 192, 0.5)",
    borderRadius: "8px",
    padding: "8px 16px",
    fontWeight: 500,
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "rgba(92, 107, 192, 0.08)",
      borderColor: "#5c6bc0",
    },
  },
  saveButton: {
    textTransform: "none",
    backgroundColor: "#3f51b5",
    borderRadius: "8px",
    padding: "8px 20px",
    fontWeight: 500,
    boxShadow: "0 3px 6px rgba(63, 81, 181, 0.2)",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#303f9f",
      boxShadow: "0 5px 10px rgba(63, 81, 181, 0.3)",
      transform: "translateY(-1px)",
    },
  },
  formGroup: {
    animation: `${slideIn} 0.4s ease-out forwards`,
    animationDelay: "0.2s",
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    padding: "10px",
    backgroundColor: "rgba(63, 81, 181, 0.05)",
    borderRadius: "10px",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "rgba(63, 81, 181, 0.08)",
    },
    animation: `${slideIn} 0.4s ease-out forwards`,
    animationDelay: "0.25s",
  },
  switchLabel: {
    marginLeft: "10px",
    fontSize: "0.875rem",
    color: "#333",
    fontWeight: 500,
  },
  dateTimeWrapper: {
    animation: `${slideIn} 0.4s ease-out forwards`,
    animationDelay: "0.2s",
    "& .MuiPickersPopper-root": {
      zIndex: 9999, // Ensure picker popover is above everything
    },
  },
  dateTimePicker: {
    width: "100%",
    "& .MuiInputBase-root": {
      borderRadius: "10px",
    },
    "& .MuiPickersPopper-root, & .MuiPickersPopper-paper": {
      zIndex: 9999, // Ensure the picker popover appears above the modal
    },
    // Customizing the date/time picker colors
    "& .MuiPickersDay-root": {
      color: "#333", // Brighter text for day numbers
      fontWeight: 500,
    },
    "& .MuiPickersDay-root:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.08)", // Light gray hover
    },
    "& .MuiPickersDay-root.Mui-selected": {
      backgroundColor: "#e0e0e0", // Light gray for selected day
      color: "#333", // Dark text for contrast
      "&:hover": {
        backgroundColor: "#d5d5d5", // Slightly darker on hover
      },
    },
    "& .MuiClock-pin, & .MuiClockPointer-root": {
      backgroundColor: "#9e9e9e", // Light gray for clock hands and center pin
    },
    "& .MuiClockPointer-thumb": {
      backgroundColor: "#9e9e9e", // Light gray for clock thumb
      border: "14px solid #9e9e9e",
    },
    "& .MuiPickersDay-today": {
      border: "1px solid #9e9e9e", // Light gray border for today
      color: "#333", // Darker text for today
    },
    "& .MuiPickersFadeTransitionGroup-root": {
      color: "#333", // Brighter text for month/year
    },
    "& .MuiPickersCalendarHeader-root": {
      color: "#333", // Brighter text for header
    },
    // Month/Year selection
    "& .MuiPickersYear-yearButton, & .MuiPickersYear-yearButton.Mui-selected": {
      color: "#333", // Brighter text for year
    },
    "& .MuiPickersYear-yearButton.Mui-selected": {
      backgroundColor: "#e0e0e0", // Light gray for selected year
    },
  },
};

export const useEventFormLogic = (initialEvent: Event, onSave: (event: Event) => void) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<Event>(initialEvent);

  useEffect(() => {
    setFormData(initialEvent);
  }, [initialEvent]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const handleCheckboxChange = useCallback((name: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      if (date) {
        // If new start date is after current end date, update end date accordingly
        if (formData.end && date > formData.end) {
          // Set end time 1 hour after the new start time
          const newEndDate = new Date(date);
          newEndDate.setHours(date.getHours() + 1);

          setFormData((prev) => ({
            ...prev,
            start: date,
            end: newEndDate,
          }));
        } else {
          // Otherwise just update the start date
          setFormData((prev) => ({
            ...prev,
            start: date,
          }));
        }
      }
    },
    [formData.end]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      if (date) {
        // If new end date is before current start date, don't update
        if (formData.start && date < formData.start) {
          // Show error message
          enqueueSnackbar("La data di fine non può essere precedente alla data di inizio", {
            variant: "error",
          });
          return;
        }

        setFormData((prev) => ({
          ...prev,
          end: date,
        }));
      }
    },
    [formData.start]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      // Validate dates before saving
      if (formData.start > formData.end) {
        enqueueSnackbar("La data di inizio non può essere successiva alla data di fine", {
          variant: "error",
        });
        return;
      }

      onSave(formData);
    },
    [formData, onSave, enqueueSnackbar]
  );

  return {
    formData,
    handleChange,
    handleCheckboxChange,
    handleStartDateChange,
    handleEndDateChange,
    handleSubmit,
    styles,
  };
};
